import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Autocomplete,
  TextField,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import {
  ScienceRounded,
  ExpandMoreRounded,
  PlayArrowRounded,
  FileUploadRounded,
  DownloadRounded,
  ClearRounded,
} from "@mui/icons-material";
import color from "../../Constants/colors";
import Loading from "../Common/Loading";
import ConfigOverrideStep from "../EvaluationPage/ConfigOverrideStep";
import { useCompanies } from "../../Query/useCompanies";
import {
  useStartRetrievalTest,
  useRetrievalTestStatus,
} from "../../Query/useRetrievalTest";
import { fetchQuestionListsForEvaluation } from "../../Api/questions";
import { useTranslation } from "react-i18next";
import { QuestionItem } from "../../Models/question";
import { Company } from "../../Models/company";
import {
  RetrievalCategoryResult,
  RetrievalSource,
} from "../../Api/retrievalTest";
import { EvaluationConfigDefaults } from "../../Models/evaluation";

type ConfigValue = number | boolean | string;

const RETRIEVAL_TEST_ALLOWED_FIELDS = new Set<keyof EvaluationConfigDefaults>([
  "reranker_enabled",
  "reranker_multiplier",
  "hybrid_search_enabled",
  "hybrid_search_alpha",
  "default_dense_company_data_retrieval_limit",
  "company_data_similarity_threshold",
  "question_embedding_strategy",
]);

const formatSource = (source: RetrievalSource): string => {
  if (source.page != null) {
    return `${source.document_name} (p. ${source.page})`;
  }
  if (source.sheet_name) {
    return `${source.document_name} [${source.sheet_name}]`;
  }
  return source.document_name;
};

const RetrievalTest: React.FC = () => {
  const { t } = useTranslation("retrievalTest");

  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedQuestionList, setSelectedQuestionList] =
    useState<QuestionItem | null>(null);
  const [questionLists, setQuestionLists] = useState<QuestionItem[]>([]);
  const [questionListsLoading, setQuestionListsLoading] = useState(false);
  const [configOverrides, setConfigOverrides] = useState<
    Record<string, ConfigValue>
  >({});
  const [testId, setTestId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [groundTruth, setGroundTruth] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [groundTruthError, setGroundTruthError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleGroundTruthUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string);
        setGroundTruth(parsed);
        setGroundTruthError(false);
      } catch {
        setGroundTruth(null);
        setGroundTruthError(true);
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleExportReport = () => {
    const reportMd = statusQuery.data?.report_md;
    if (!reportMd) return;
    const blob = new Blob([reportMd], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `retrieval_report_${new Date().toISOString().slice(0, 19).replace(/[:.]/g, "-")}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const companiesQuery = useCompanies(1, 1000, "name", "asc", "");
  const startTestMutation = useStartRetrievalTest();
  const statusQuery = useRetrievalTestStatus(testId, isPolling);

  const companies: Company[] = companiesQuery.data?.companies ?? [];

  useEffect(() => {
    if (
      statusQuery.data?.status === "completed" ||
      statusQuery.data?.status === "error"
    ) {
      setIsPolling(false);
    }
  }, [statusQuery.data?.status]);

  useEffect(() => {
    if (!selectedCompany) {
      setQuestionLists([]);
      setSelectedQuestionList(null);
      return;
    }

    const loadQuestionLists = async () => {
      setQuestionListsLoading(true);
      try {
        const data = await fetchQuestionListsForEvaluation();
        setQuestionLists(data.question_lists);
      } catch {
        setQuestionLists([]);
      } finally {
        setQuestionListsLoading(false);
      }
    };

    loadQuestionLists();
  }, [selectedCompany]);

  const handleRun = async () => {
    if (!selectedCompany || !selectedQuestionList) return;
    try {
      const result = await startTestMutation.mutateAsync({
        companyId: selectedCompany.id,
        questionListId: selectedQuestionList.id,
        configOverrides,
        groundTruth,
      });
      setTestId(result.test_id);
      setIsPolling(true);
    } catch {
      // snackbar handled by API layer
    }
  };

  const isRunning =
    isPolling ||
    statusQuery.data?.status === "running" ||
    statusQuery.data?.status === "queued";
  const isCompleted = statusQuery.data?.status === "completed";
  const isError = statusQuery.data?.status === "error";
  const canRun =
    !!selectedCompany &&
    !!selectedQuestionList &&
    !isRunning &&
    !startTestMutation.isPending;

  if (companiesQuery.isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={color.accent}
        color2={color.secondary}
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        backgroundColor: color.primary,
        py: 4,
        px: 2,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              mb: 0.5,
            }}
          >
            <ScienceRounded sx={{ fontSize: "2rem", color: color.secondary }} />
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: "1.75rem",
                color: color.secondary,
              }}
            >
              {t("retrievalTest.title")}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.85rem", color: color.gray500, mt: 1 }}>
            {t("retrievalTest.description")}
          </Typography>
        </Box>

        {/* Selection Panel */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: "14px",
            border: `1px solid ${color.gray200}`,
            backgroundColor: color.primary,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              mb: 2,
            }}
          >
            {/* Company Selector */}
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => `${option.name} (ID: ${option.id})`}
              value={selectedCompany}
              onChange={(_, value) => {
                setSelectedCompany(value);
                setSelectedQuestionList(null);
                setTestId(null);
              }}
              componentsProps={{
                paper: {
                  sx: {
                    "& .MuiAutocomplete-option": { color: color.primary_dark },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("retrievalTest.selectCompany")}
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: color.secondary,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: color.secondary,
                      },
                    },
                    "& .MuiInputBase-input": { color: color.primary_dark },
                    "& .MuiInputLabel-root": { color: color.gray500 },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: color.secondary,
                    },
                  }}
                />
              )}
              disabled={isRunning}
            />

            {/* Question List Selector */}
            <Autocomplete
              options={questionLists}
              getOptionLabel={(option) =>
                `${option.name}${option.category?.name ? ` (${option.category.name})` : ""}`
              }
              value={selectedQuestionList}
              onChange={(_, value) => {
                setSelectedQuestionList(value);
                setTestId(null);
              }}
              loading={questionListsLoading}
              disabled={!selectedCompany || isRunning}
              componentsProps={{
                paper: {
                  sx: {
                    "& .MuiAutocomplete-option": { color: color.primary_dark },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("retrievalTest.selectQuestionList")}
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: color.secondary,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: color.secondary,
                      },
                    },
                    "& .MuiInputBase-input": { color: color.primary_dark },
                    "& .MuiInputLabel-root": { color: color.gray500 },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: color.secondary,
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* Run Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleRun}
              disabled={!canRun}
              startIcon={<PlayArrowRounded />}
              sx={{
                backgroundColor: color.secondary,
                color: color.white,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                "&:hover": { backgroundColor: color.accent },
                "&.Mui-disabled": {
                  backgroundColor: color.gray200,
                  color: color.gray500,
                },
              }}
            >
              {startTestMutation.isPending
                ? "Starting..."
                : t("retrievalTest.runTest")}
            </Button>
          </Box>
        </Paper>

        {/* Config Overrides */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: "14px",
            border: `1px solid ${color.gray200}`,
            backgroundColor: color.primary,
            overflow: "hidden",
          }}
        >
          <Accordion
            defaultExpanded={false}
            elevation={0}
            disableGutters
            sx={{
              backgroundColor: "transparent",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreRounded sx={{ color: color.primary_dark }} />
              }
              sx={{ px: 3, py: 1 }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: color.primary_dark,
                  fontSize: "1rem",
                }}
              >
                {t("retrievalTest.configOverrides")}
              </Typography>
              {Object.keys(configOverrides).length > 0 && (
                <Chip
                  label={`${Object.keys(configOverrides).length} changed`}
                  size="small"
                  sx={{
                    ml: 2,
                    backgroundColor: color.orange50,
                    color: color.orange600,
                    fontWeight: 600,
                    border: `1px solid ${color.orange200}`,
                  }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
              <Typography variant="body2" sx={{ color: color.gray500, mb: 2 }}>
                {t("retrievalTest.configOverridesDescription")}
              </Typography>
              <ConfigOverrideStep
                configOverrides={configOverrides}
                onConfigChange={setConfigOverrides}
                allowedFields={RETRIEVAL_TEST_ALLOWED_FIELDS}
              />
            </AccordionDetails>
          </Accordion>
        </Paper>

        {/* Ground Truth Upload */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: "14px",
            border: `1px solid ${color.gray200}`,
            backgroundColor: color.primary,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              color: color.primary_dark,
              fontSize: "1rem",
              mb: 1,
            }}
          >
            {t("retrievalTest.groundTruth")}
          </Typography>
          <Typography variant="body2" sx={{ color: color.gray500, mb: 2 }}>
            {t("retrievalTest.groundTruthDescription")}
          </Typography>
          <input
            type="file"
            accept=".json"
            ref={fileInputRef}
            onChange={handleGroundTruthUpload}
            style={{ display: "none" }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileUploadRounded />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isRunning}
              sx={{
                borderColor: color.secondary,
                color: color.secondary,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  borderColor: color.accent,
                  backgroundColor: color.blue50,
                },
              }}
            >
              {t("retrievalTest.uploadGroundTruth")}
            </Button>
            {groundTruth ? (
              <>
                <Chip
                  label={t("retrievalTest.groundTruthLoaded", {
                    count: Object.keys(groundTruth).length,
                  })}
                  size="small"
                  sx={{
                    backgroundColor: color.green50,
                    color: color.green700,
                    fontWeight: 500,
                    border: `1px solid ${color.green200}`,
                  }}
                />
                <Button
                  size="small"
                  startIcon={<ClearRounded />}
                  onClick={() => setGroundTruth(null)}
                  sx={{
                    color: color.gray500,
                    textTransform: "none",
                    minWidth: "auto",
                  }}
                >
                  {t("retrievalTest.groundTruthClear")}
                </Button>
              </>
            ) : (
              <Chip
                label={t("retrievalTest.groundTruthDefault")}
                size="small"
                sx={{
                  backgroundColor: color.blue50,
                  color: color.blue700,
                  fontWeight: 500,
                  border: `1px solid ${color.blue200}`,
                }}
              />
            )}
            {groundTruthError && (
              <Typography sx={{ color: color.red600, fontSize: "0.85rem" }}>
                {t("retrievalTest.groundTruthError")}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Progress */}
        {isRunning && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: "14px",
              border: `1px solid ${color.gray200}`,
              backgroundColor: color.primary,
            }}
          >
            <Typography sx={{ fontWeight: 600, color: color.secondary, mb: 1 }}>
              {t("retrievalTest.running")}
            </Typography>
            <LinearProgress
              sx={{
                mb: 1,
                borderRadius: 4,
                height: 8,
                backgroundColor: color.gray200,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: color.secondary,
                },
              }}
            />
            {statusQuery.data?.progress && (
              <Typography sx={{ fontSize: "0.85rem", color: color.gray500 }}>
                {t("retrievalTest.progress", {
                  progress: statusQuery.data.progress,
                })}
              </Typography>
            )}
          </Paper>
        )}

        {/* Error */}
        {isError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: "14px" }}>
            {t("retrievalTest.errorMessage", {
              message:
                statusQuery.data?.error_message || t("retrievalTest.error"),
            })}
          </Alert>
        )}

        {/* Results */}
        {isCompleted && statusQuery.data?.results && (
          <>
            {/* Config Used */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: "14px",
                border: `1px solid ${color.gray200}`,
                borderLeft: `4px solid ${color.secondary}`,
                backgroundColor: color.primary,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: color.primary_dark,
                  mb: 1,
                  fontSize: "1rem",
                }}
              >
                {t("retrievalTest.configUsed")}
              </Typography>
              {statusQuery.data.config_used &&
              Object.keys(statusQuery.data.config_used).length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {Object.entries(statusQuery.data.config_used).map(
                    ([key, value]) => (
                      <Chip
                        key={key}
                        label={`${key}: ${value}`}
                        size="small"
                        sx={{
                          backgroundColor: color.orange50,
                          color: color.orange600,
                          fontWeight: 500,
                          border: `1px solid ${color.orange200}`,
                          fontFamily: "'Roboto Mono', monospace",
                          fontSize: "0.75rem",
                        }}
                      />
                    ),
                  )}
                </Box>
              ) : (
                <Typography sx={{ fontSize: "0.85rem", color: color.gray500 }}>
                  {t("retrievalTest.noOverrides")}
                </Typography>
              )}
            </Paper>

            {/* Completed Banner */}
            <Alert severity="success" sx={{ mb: 3, borderRadius: "14px" }}>
              {t("retrievalTest.completed")}
            </Alert>

            {/* Metrics Summary */}
            {statusQuery.data.metrics && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  borderRadius: "14px",
                  border: `1px solid ${color.gray200}`,
                  borderLeft: `4px solid ${color.accent}`,
                  backgroundColor: color.primary,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: color.primary_dark,
                    mb: 2,
                    fontSize: "1rem",
                  }}
                >
                  {t("retrievalTest.metricsTitle")}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr 1fr",
                      md: "1fr 1fr 1fr 1fr",
                    },
                    gap: 2,
                  }}
                >
                  {[
                    {
                      label: t("retrievalTest.metricTotalQuestions"),
                      value: statusQuery.data.metrics.total_questions,
                    },
                    {
                      label: t("retrievalTest.metricTotalSources"),
                      value: statusQuery.data.metrics.total_sources,
                    },
                    {
                      label: t("retrievalTest.metricAvgSources"),
                      value: statusQuery.data.metrics.avg_sources_per_question,
                    },
                    {
                      label: t("retrievalTest.metricUniqueDocuments"),
                      value: statusQuery.data.metrics.unique_documents,
                    },
                  ].map((metric) => (
                    <Box
                      key={metric.label}
                      sx={{
                        textAlign: "center",
                        p: 1.5,
                        borderRadius: "10px",
                        backgroundColor: color.gray50,
                        border: `1px solid ${color.gray200}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "1.5rem",
                          fontWeight: 700,
                          color: color.secondary,
                          fontFamily: "'Roboto Mono', monospace",
                        }}
                      >
                        {metric.value}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          color: color.gray500,
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {metric.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}

            {/* Recall Metrics (when ground truth was provided) */}
            {statusQuery.data.recall_metrics &&
              statusQuery.data.recall_metrics.total_evaluated > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: "14px",
                    border: `1px solid ${color.gray200}`,
                    borderLeft: `4px solid ${color.green700}`,
                    backgroundColor: color.primary,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: color.primary_dark,
                      mb: 2,
                      fontSize: "1rem",
                    }}
                  >
                    {t("retrievalTest.recallTitle")}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr 1fr",
                        md: "1fr 1fr 1fr 1fr 1fr 1fr",
                      },
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    {[
                      {
                        label: t("retrievalTest.recallPassed"),
                        value: statusQuery.data.recall_metrics.passed,
                        valueColor: color.green700,
                      },
                      {
                        label: t("retrievalTest.recallFailed"),
                        value: statusQuery.data.recall_metrics.failed,
                        valueColor:
                          statusQuery.data.recall_metrics.failed > 0
                            ? color.red600
                            : color.primary_dark,
                      },
                      {
                        label: t("retrievalTest.recallPassPct"),
                        value: `${statusQuery.data.recall_metrics.pass_pct}%`,
                        valueColor: color.secondary,
                      },
                      {
                        label: t("retrievalTest.recallAvgRecall"),
                        value: `${statusQuery.data.recall_metrics.avg_recall}%`,
                        valueColor: color.secondary,
                      },
                      {
                        label: t("retrievalTest.recallOverallRecall"),
                        value: `${statusQuery.data.recall_metrics.overall_recall}%`,
                        valueColor: color.secondary,
                      },
                      {
                        label: t("retrievalTest.recallFoundRefs"),
                        value: `${statusQuery.data.recall_metrics.total_found_refs}/${statusQuery.data.recall_metrics.total_expected_refs}`,
                        valueColor: color.primary_dark,
                      },
                    ].map((metric) => (
                      <Box
                        key={metric.label}
                        sx={{
                          textAlign: "center",
                          p: 1.5,
                          borderRadius: "10px",
                          backgroundColor: color.gray50,
                          border: `1px solid ${color.gray200}`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "1.25rem",
                            fontWeight: 700,
                            color: metric.valueColor,
                            fontFamily: "'Roboto Mono', monospace",
                          }}
                        >
                          {metric.value}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.68rem",
                            color: color.gray500,
                            fontWeight: 500,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          {metric.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Per-category recall table */}
                  <TableContainer
                    sx={{
                      borderRadius: "10px",
                      border: `1px solid ${color.gray200}`,
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: color.gray50 }}>
                          {[
                            t("retrievalTest.category"),
                            "Total",
                            t("retrievalTest.recallPassed"),
                            t("retrievalTest.recallFailed"),
                            t("retrievalTest.recallAvgRecall"),
                          ].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                textTransform: "uppercase",
                                color: color.primary_dark,
                                border: "none",
                                py: 1,
                              }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(
                          statusQuery.data.recall_metrics.per_category,
                        ).map(([cat, cs]) => (
                          <TableRow key={cat}>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: color.primary_dark,
                                border: "none",
                                fontSize: "0.8rem",
                              }}
                            >
                              {cat}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: color.primary_dark,
                                border: "none",
                                fontSize: "0.8rem",
                              }}
                            >
                              {cs.total}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: color.green700,
                                fontWeight: 600,
                                border: "none",
                                fontSize: "0.8rem",
                              }}
                            >
                              {cs.passed}
                            </TableCell>
                            <TableCell
                              sx={{
                                color:
                                  cs.failed > 0
                                    ? color.red600
                                    : color.primary_dark,
                                fontWeight: 600,
                                border: "none",
                                fontSize: "0.8rem",
                              }}
                            >
                              {cs.failed}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: color.secondary,
                                fontWeight: 600,
                                border: "none",
                                fontSize: "0.8rem",
                              }}
                            >
                              {cs.avg_recall}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

            {/* Export Report Button */}
            {statusQuery.data.report_md && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadRounded />}
                  onClick={handleExportReport}
                  sx={{
                    borderColor: color.secondary,
                    color: color.secondary,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: color.accent,
                      backgroundColor: color.blue50,
                    },
                  }}
                >
                  {t("retrievalTest.exportReport")}
                </Button>
              </Box>
            )}

            {/* Results by Category */}
            {Object.entries(statusQuery.data.results).map(
              ([categoryKey, categoryData]: [
                string,
                RetrievalCategoryResult,
              ]) => {
                const questions = categoryData.questions;
                const questionCount = Object.keys(questions).length;
                const totalSources = Object.values(questions).reduce(
                  (sum, q) => sum + q.sources.length,
                  0,
                );
                const catMetrics =
                  statusQuery.data?.metrics?.per_category?.[categoryKey];
                const catRecall =
                  statusQuery.data?.recall_metrics?.per_category?.[categoryKey];
                const recallPerQuestion =
                  statusQuery.data?.recall_metrics?.per_question;

                return (
                  <Accordion
                    key={categoryKey}
                    defaultExpanded={false}
                    elevation={0}
                    disableGutters
                    sx={{
                      mb: 2,
                      borderRadius: "14px !important",
                      border: `1px solid ${color.gray200}`,
                      "&:before": { display: "none" },
                      overflow: "hidden",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreRounded sx={{ color: color.primary_dark }} />
                      }
                      sx={{
                        px: 3,
                        backgroundColor: color.primary,
                        "& .MuiAccordionSummary-content": {
                          my: 1.5,
                          alignItems: "center",
                          gap: 2,
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: color.primary_dark,
                          fontSize: "1rem",
                        }}
                      >
                        {categoryData.category_name || categoryKey}
                      </Typography>
                      <Chip
                        label={t("retrievalTest.totalQuestions", {
                          count: questionCount,
                        })}
                        size="small"
                        sx={{
                          backgroundColor: color.blue50,
                          color: color.secondary,
                          fontWeight: 500,
                          border: `1px solid ${color.blue200}`,
                        }}
                      />
                      <Chip
                        label={t("retrievalTest.totalSources", {
                          count: totalSources,
                        })}
                        size="small"
                        sx={{
                          backgroundColor: color.gray50,
                          color: color.gray500,
                          fontWeight: 500,
                          border: `1px solid ${color.gray200}`,
                        }}
                      />
                      {catMetrics && (
                        <Chip
                          label={`avg ${catMetrics.avg_sources_per_question}/q`}
                          size="small"
                          sx={{
                            backgroundColor: color.orange50,
                            color: color.orange600,
                            fontWeight: 500,
                            border: `1px solid ${color.orange200}`,
                            fontSize: "0.72rem",
                          }}
                        />
                      )}
                      {catRecall && (
                        <Chip
                          label={`recall ${catRecall.avg_recall}%`}
                          size="small"
                          sx={{
                            backgroundColor:
                              catRecall.failed === 0
                                ? color.green50
                                : color.red50,
                            color:
                              catRecall.failed === 0
                                ? color.green700
                                : color.red600,
                            fontWeight: 600,
                            border: `1px solid ${
                              catRecall.failed === 0
                                ? color.green200
                                : color.red200
                            }`,
                            fontSize: "0.72rem",
                          }}
                        />
                      )}
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: color.secondary }}>
                              <TableCell
                                sx={{
                                  color: color.white,
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.06em",
                                  border: "none",
                                  py: 1.5,
                                  pl: 3,
                                  width: 80,
                                }}
                              >
                                {t("retrievalTest.questionId")}
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: color.white,
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.06em",
                                  border: "none",
                                  py: 1.5,
                                }}
                              >
                                {t("retrievalTest.questionText")}
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: color.white,
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.06em",
                                  border: "none",
                                  py: 1.5,
                                  width: 80,
                                  textAlign: "center",
                                }}
                              >
                                {t("retrievalTest.sourcesCount")}
                              </TableCell>
                              {recallPerQuestion && (
                                <TableCell
                                  sx={{
                                    color: color.white,
                                    fontWeight: 700,
                                    fontSize: "0.75rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                    border: "none",
                                    py: 1.5,
                                    width: 100,
                                    textAlign: "center",
                                  }}
                                >
                                  {t("retrievalTest.recallColumn")}
                                </TableCell>
                              )}
                              <TableCell
                                sx={{
                                  color: color.white,
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.06em",
                                  border: "none",
                                  py: 1.5,
                                  pr: 3,
                                }}
                              >
                                {t("retrievalTest.retrievedSources")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(questions).map(
                              ([questionKey, questionData], idx) => (
                                <TableRow
                                  key={questionKey}
                                  sx={{
                                    backgroundColor:
                                      idx % 2 === 0
                                        ? color.primary
                                        : color.gray50,
                                    "&:hover": {
                                      backgroundColor: color.blue50,
                                    },
                                    transition: "background-color 0.15s",
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      fontFamily: "'Roboto Mono', monospace",
                                      fontSize: "0.8rem",
                                      fontWeight: 600,
                                      color: color.primary_dark,
                                      border: "none",
                                      py: 1.5,
                                      pl: 3,
                                      verticalAlign: "top",
                                    }}
                                  >
                                    {questionData.question_id}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: "0.8rem",
                                      color: color.primary_dark,
                                      border: "none",
                                      py: 1.5,
                                      verticalAlign: "top",
                                      maxWidth: 400,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "0.8rem",
                                        lineHeight: 1.5,
                                        wordBreak: "break-word",
                                        color: color.primary_dark,
                                      }}
                                    >
                                      {questionData.question_text}
                                    </Typography>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontFamily: "'Roboto Mono', monospace",
                                      fontSize: "0.8rem",
                                      fontWeight: 700,
                                      color: color.secondary,
                                      border: "none",
                                      py: 1.5,
                                      verticalAlign: "top",
                                      textAlign: "center",
                                    }}
                                  >
                                    {questionData.sources_count}
                                  </TableCell>
                                  {recallPerQuestion &&
                                    (() => {
                                      const qRecall = recallPerQuestion.find(
                                        (r) =>
                                          r.category_key === categoryKey &&
                                          r.question_key === questionKey,
                                      );
                                      return (
                                        <TableCell
                                          sx={{
                                            border: "none",
                                            py: 1.5,
                                            verticalAlign: "top",
                                            textAlign: "center",
                                          }}
                                        >
                                          {qRecall ? (
                                            <Box>
                                              <Chip
                                                label={
                                                  qRecall.all_found
                                                    ? t(
                                                        "retrievalTest.recallPass",
                                                      )
                                                    : t(
                                                        "retrievalTest.recallFail",
                                                      )
                                                }
                                                size="small"
                                                sx={{
                                                  backgroundColor:
                                                    qRecall.all_found
                                                      ? color.green50
                                                      : color.red50,
                                                  color: qRecall.all_found
                                                    ? color.green700
                                                    : color.red600,
                                                  fontWeight: 700,
                                                  fontSize: "0.68rem",
                                                  border: `1px solid ${
                                                    qRecall.all_found
                                                      ? color.green200
                                                      : color.red200
                                                  }`,
                                                  mb: 0.5,
                                                }}
                                              />
                                              <Typography
                                                sx={{
                                                  fontSize: "0.72rem",
                                                  fontFamily:
                                                    "'Roboto Mono', monospace",
                                                  color: color.primary_dark,
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {qRecall.found_count}/
                                                {qRecall.expected_count} (
                                                {qRecall.recall}%)
                                              </Typography>
                                            </Box>
                                          ) : (
                                            <Typography
                                              sx={{
                                                fontSize: "0.72rem",
                                                color: color.gray400,
                                              }}
                                            >
                                              —
                                            </Typography>
                                          )}
                                        </TableCell>
                                      );
                                    })()}
                                  <TableCell
                                    sx={{
                                      border: "none",
                                      py: 1.5,
                                      pr: 3,
                                      verticalAlign: "top",
                                    }}
                                  >
                                    {(() => {
                                      const qRecallData =
                                        recallPerQuestion?.find(
                                          (r) =>
                                            r.category_key === categoryKey &&
                                            r.question_key === questionKey,
                                        );
                                      if (qRecallData) {
                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: 1,
                                            }}
                                          >
                                            {/* Expected */}
                                            <Box>
                                              <Typography
                                                sx={{
                                                  fontSize: "0.68rem",
                                                  fontWeight: 700,
                                                  color: color.gray500,
                                                  textTransform: "uppercase",
                                                  mb: 0.5,
                                                }}
                                              >
                                                {t(
                                                  "retrievalTest.recallExpected",
                                                )}{" "}
                                                (
                                                {
                                                  qRecallData.expected_sources
                                                    .length
                                                }
                                                )
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  flexWrap: "wrap",
                                                  gap: 0.5,
                                                }}
                                              >
                                                {qRecallData.expected_sources.map(
                                                  (s, i) => (
                                                    <Chip
                                                      key={i}
                                                      label={formatSource(s)}
                                                      size="small"
                                                      sx={{
                                                        backgroundColor:
                                                          color.blue50,
                                                        color: color.secondary,
                                                        fontWeight: 500,
                                                        fontSize: "0.72rem",
                                                        border: `1px solid ${color.blue200}`,
                                                        fontFamily:
                                                          "'Roboto Mono', monospace",
                                                      }}
                                                    />
                                                  ),
                                                )}
                                              </Box>
                                            </Box>
                                            {/* Found */}
                                            <Box>
                                              <Typography
                                                sx={{
                                                  fontSize: "0.68rem",
                                                  fontWeight: 700,
                                                  color: color.green700,
                                                  textTransform: "uppercase",
                                                  mb: 0.5,
                                                }}
                                              >
                                                {t("retrievalTest.recallFound")}{" "}
                                                ({qRecallData.found.length})
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  flexWrap: "wrap",
                                                  gap: 0.5,
                                                }}
                                              >
                                                {qRecallData.found.length >
                                                0 ? (
                                                  qRecallData.found.map(
                                                    (s, i) => (
                                                      <Chip
                                                        key={i}
                                                        label={formatSource(s)}
                                                        size="small"
                                                        sx={{
                                                          backgroundColor:
                                                            color.green50,
                                                          color: color.green700,
                                                          fontWeight: 500,
                                                          fontSize: "0.72rem",
                                                          border: `1px solid ${color.green200}`,
                                                          fontFamily:
                                                            "'Roboto Mono', monospace",
                                                        }}
                                                      />
                                                    ),
                                                  )
                                                ) : (
                                                  <Typography
                                                    sx={{
                                                      fontSize: "0.72rem",
                                                      color: color.gray400,
                                                      fontStyle: "italic",
                                                    }}
                                                  >
                                                    —
                                                  </Typography>
                                                )}
                                              </Box>
                                            </Box>
                                            {/* Missing */}
                                            {qRecallData.missing.length > 0 && (
                                              <Box>
                                                <Typography
                                                  sx={{
                                                    fontSize: "0.68rem",
                                                    fontWeight: 700,
                                                    color: color.red600,
                                                    textTransform: "uppercase",
                                                    mb: 0.5,
                                                  }}
                                                >
                                                  {t(
                                                    "retrievalTest.recallMissing",
                                                  )}{" "}
                                                  ({qRecallData.missing.length})
                                                </Typography>
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 0.5,
                                                  }}
                                                >
                                                  {qRecallData.missing.map(
                                                    (s, i) => (
                                                      <Chip
                                                        key={i}
                                                        label={formatSource(s)}
                                                        size="small"
                                                        sx={{
                                                          backgroundColor:
                                                            color.red50,
                                                          color: color.red600,
                                                          fontWeight: 500,
                                                          fontSize: "0.72rem",
                                                          border: `1px solid ${color.red200}`,
                                                          fontFamily:
                                                            "'Roboto Mono', monospace",
                                                        }}
                                                      />
                                                    ),
                                                  )}
                                                </Box>
                                              </Box>
                                            )}
                                          </Box>
                                        );
                                      }
                                      return questionData.sources.length > 0 ? (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 0.5,
                                          }}
                                        >
                                          {questionData.sources.map(
                                            (source, sIdx) => (
                                              <Chip
                                                key={sIdx}
                                                label={formatSource(source)}
                                                size="small"
                                                sx={{
                                                  backgroundColor: color.blue50,
                                                  color: color.secondary,
                                                  fontWeight: 500,
                                                  fontSize: "0.72rem",
                                                  border: `1px solid ${color.blue200}`,
                                                  fontFamily:
                                                    "'Roboto Mono', monospace",
                                                }}
                                              />
                                            ),
                                          )}
                                        </Box>
                                      ) : (
                                        <Typography
                                          sx={{
                                            fontSize: "0.8rem",
                                            color: color.gray400,
                                            fontStyle: "italic",
                                          }}
                                        >
                                          {t("retrievalTest.noSources")}
                                        </Typography>
                                      );
                                    })()}
                                  </TableCell>
                                </TableRow>
                              ),
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                );
              },
            )}
          </>
        )}

        {/* Empty state */}
        {!isRunning && !isCompleted && !isError && (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              borderRadius: "14px",
              border: `1px solid ${color.gray200}`,
              backgroundColor: color.primary,
              textAlign: "center",
            }}
          >
            <ScienceRounded
              sx={{ fontSize: "3rem", color: color.gray200, mb: 1 }}
            />
            <Typography sx={{ color: color.gray500, fontSize: "0.9rem" }}>
              {t("retrievalTest.noResults")}
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default RetrievalTest;
